import {
    EmbedEvent,
    EmbedEvents,
    QueueUpdatedEvent,
} from 'embed/types/events'
import {
    NinchatProperties,
    NinchatStore,
} from 'api/types'
import {NinchatQueueUpdatedEvent} from 'api/types/events'
import {QueueID} from 'api/types/properties'
import {RootState} from 'store'
import {composeQueueRequest} from 'embed/utils/metadata'
import {config} from 'embed/listeners'
import {describeTranscript} from 'api/action/describeTranscript'
import {getGroupQueues} from 'selectors/ninchat/groups'
import {getQueueActivity} from 'api/action/getQueueActivity'
import {getTicketDuration} from 'hooks/queueTime/useTicketDuration'
import {ninchatEventSlice} from 'api/slice'
import {startAppListening} from 'middlewares/listener'

type Effect = Parameters<typeof startAppListening>[0]['effect']
type Predicate = Parameters<typeof startAppListening>[0]['predicate']
type ListenerApi = Parameters<Effect>[1]

const queueUpdatedType = `${ninchatEventSlice.actions.queue_updated}`

/**
 * Fetches fresh activity and transcript data for a queue's ticket duration
 * calculation.  Activity is always re-fetched when unassigned tickets exist
 * because a queue_updated event signals queue state has changed and cached
 * activity may be stale (e.g. a new ticket arrived or the previously-oldest
 * ticket was picked up, shifting which entry is now pending).
 *
 * @returns fresh state after all fetches or the original state if there's no
 * tickets to track.
 */
const loadTicketingState = async (queue_id: QueueID, state: RootState, listenerApi: ListenerApi): Promise<RootState> => {
    const queue = state.ninchat.user_queues?.[queue_id] ?? state.ninchat._queues?.[queue_id]

    if (!queue) {
        return state
    }

    const queuesNeedingActivity: QueueID[] = queue.queue_attrs.is_group
        ? getGroupQueues(state, true, queue_id)
            .filter((child) => (child.queueProps.queue_attrs.unassigned_registered ?? 0) > 0)
            .map((child) => child.id)
        : (queue.queue_attrs.unassigned_registered ?? 0) > 0
            ? [queue_id]
            : []

    if (queuesNeedingActivity.length === 0) {
        return state
    }

    await Promise.allSettled(queuesNeedingActivity.map((id) => listenerApi.dispatch(getQueueActivity({queue_id: id}))))

    const stateAfterActivity = listenerApi.getState()
    const existingTranscripts: NinchatStore.NinchatAudienceTranscriptsStore = {
        ...stateAfterActivity.ninchat._audience_transcripts,
        ...stateAfterActivity.ninchat._user_audience_transcripts,
    }

    const audiencesNeedingTranscripts = [
        ...new Set(
            queuesNeedingActivity.flatMap((id) => (stateAfterActivity.ninchat._user_queues_activity[id] ?? [])
                .filter((a) => a.event === 'audience_complete' && a.audience_id)
                .map((a) => a.audience_id as NinchatProperties.AudienceID)
                .filter((audienceId) => !existingTranscripts[audienceId])),
        ),
    ]

    if (audiencesNeedingTranscripts.length > 0) {
        await Promise.allSettled(audiencesNeedingTranscripts.map((audienceId) => listenerApi.dispatch(describeTranscript({audience_id: audienceId}))))
    }

    return listenerApi.getState()
}

export const buildQueueUpdatedEvent = (
    queue_id: QueueID,
    state: RootState,
    targetOrigin: string,
): QueueUpdatedEvent | undefined => {
    const queue = state.ninchat.user_queues?.[queue_id] ?? state.ninchat._queues?.[queue_id]

    if (!queue) {
        return undefined
    }

    const isGroupQueue = !!queue.queue_attrs.is_group
    const groupQueues = getGroupQueues(state, isGroupQueue, queue_id)
    const parentGroupQueueId = queue.queue_attrs.group_id
    const parentGroupQueue = parentGroupQueueId
        ? state.ninchat.user_queues?.[parentGroupQueueId] ?? state.ninchat._queues?.[parentGroupQueueId]
        : undefined

    const ticketDuration = getTicketDuration({
        audienceComments: state.ninchat._user_audience_comments,
        audienceTranscripts: {
            ...state.ninchat._audience_transcripts,
            ...state.ninchat._user_audience_transcripts,
        },
        groupQueues,
        parentGroupQueue,
        queue,
        queueActivities: state.ninchat._user_queues_activity,
        queueId: queue_id,
    })

    return {
        delivery_time: queue.delivery_time,
        event: EmbedEvent.QueueUpdated,
        queue_attrs: {
            group_id: queue.queue_attrs.group_id,
            is_group: queue.queue_attrs.is_group,
            length: queue.queue_attrs.length,
            name: queue.queue_attrs.name,
            unassigned_registered: queue.queue_attrs.unassigned_registered,
        },
        queue_id,
        queue_time: queue.queue_time,
        realm_id: queue.realm_id,
        targetOrigin,
        ...ticketDuration.queue_time && {unassigned_registered_time: ticketDuration.queue_time},
    }
}

export const composeQueueUpdatedEvent = (
    queueUpdate: Pick<NinchatQueueUpdatedEvent, 'queue_id' | 'queue_request'>,
    state: RootState,
    targetOrigin: string,
): QueueUpdatedEvent | undefined => {
    const event = buildQueueUpdatedEvent(queueUpdate.queue_id, state, targetOrigin)

    if (!event) {
        return undefined
    }

    return {
        ...event,
        ...queueUpdate.queue_request && {queue_request: composeQueueRequest(queueUpdate.queue_request, targetOrigin)},
    }
}

export const queueUpdatedListener: (sendToParent: (event: EmbedEvents) => void) => {effect: Effect, predicate: Predicate} = (sendToParent) => ({
    effect: async (action, listenerApi) => {
        const targetOrigin = config.get('targetOrigin')

        if (!targetOrigin) {
            console.error('missing targetOrigin')
            return
        }

        const queueUpdate = action.payload as NinchatQueueUpdatedEvent
        const {queue_id} = queueUpdate

        const state = await loadTicketingState(queue_id, listenerApi.getState(), listenerApi)
        const event = composeQueueUpdatedEvent(queueUpdate, state, targetOrigin)

        if (!event) {
            return
        }

        sendToParent(event)

        // If this is a child queue, also send for the parent group queue since ticket
        // duration is aggregated at the group level.
        const queue = state.ninchat.user_queues?.[queue_id] ?? state.ninchat._queues?.[queue_id]
        const parentGroupQueueId = queue?.queue_attrs.group_id

        if (parentGroupQueueId) {
            const parentEvent = buildQueueUpdatedEvent(parentGroupQueueId, state, targetOrigin)

            if (parentEvent) {
                sendToParent(parentEvent)
            }
        }
    },
    predicate: (action) => action.type === queueUpdatedType,
})

export const start = (sendToParent: (event: EmbedEvents) => void) => [
    startAppListening(queueUpdatedListener(sendToParent)),
]
